import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Eye, EyeOff, Hexagon, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] relative z-10">
      <section className="hidden lg:flex flex-col justify-between p-12 border-r border-zinc-800 bg-zinc-950">
        <div />
        <div>
          <div className="overline-label mb-4">Motor proprietário em Rust</div>
          <h1 className="font-display text-5xl xl:text-6xl font-bold tracking-tighter leading-none">
            Dados e vetores.<br />
            <span className="text-orange-500">RAM mínima.</span>
          </h1>
          <p className="mt-6 text-zinc-400 max-w-md leading-relaxed">
            Chave-valor ACID com WAL e busca vetorial HNSW quantizada, tudo em arquivos mapeados. Cada banco com sua própria API.
          </p>
        </div>
        <div className="grid grid-cols-3 border border-zinc-800 divide-x divide-zinc-800 text-sm">
          {[["1.8 MB", "heap p/ 200k vetores"], ["1.000", "recall@10"], ["860k/s", "escritas KV"]].map(([v, l]) => (
            <div key={l} className="p-4">
              <div className="font-display text-2xl font-bold">{v}</div>
              <div className="text-zinc-500 text-xs mt-1">{l}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-9 w-9 grid place-items-center bg-orange-600 rounded-sm">
              <Hexagon className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-bold text-xl tracking-tight">AxonDB</span>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">E-mail</Label>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              placeholder="voce@exemplo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="login-email-input"
              className="bg-transparent border-zinc-800 rounded-sm h-11 placeholder:text-zinc-600"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Senha</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder="Digite sua senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="bg-transparent border-zinc-800 rounded-sm h-11 pr-11 placeholder:text-zinc-600"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                data-testid="login-toggle-password-button"
                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                className="absolute inset-y-0 right-0 px-3 grid place-items-center text-zinc-500 hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {error && (
            <div className="text-sm text-red-400 border border-red-900/60 bg-red-950/40 px-3 py-2 rounded-sm" data-testid="login-error">
              {error}
            </div>
          )}
          <Button type="submit" disabled={busy} data-testid="login-submit-button" className="w-full h-11 bg-orange-600 hover:bg-orange-500 text-white rounded-sm btn-tactile font-medium">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Entrar
          </Button>
        </form>
      </section>
    </div>
  );
}
